import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Download,
  ArrowRight,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import cosmeticBrochureImg from "../../../assets/Images/browchure/COSMETIC-BROCHURE-SILVER-2-1-scaled.png";
import nfplPresentationImg from "../../../assets/Images/browchure/NFPL-PRESENTATION-2025-1-scaled.png";
import nutralikeImg from "../../../assets/Images/browchure/nutralike.jpg";
import brochurePdf from "../../../assets/COSMETICS BROCHURE.pdf";
import productlist from "../../../assets/PRODUCT LIST_MAY_2026_SEGMENT WISE.pdf";
import presentation from "../../../assets/NFPL PRESENTATION_25.05.2026.pdf";


const triggerDownload = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const slugifyFileName = (str) =>
  String(str || "brochure")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// ── EmailJS credentials ───────────────────────────────────────────────
const SERVICE_ID  = "service_gnmq7iu";
const TEMPLATE_ID = "template_lu4mjl9";
const PUBLIC_KEY  = "adDZaNUl_gM8HMMV0";
// ──────────────────────────────────────────────────────────────────────

const INITIAL = {
  firstName: "",
  company: "",
  countryCode: "+91",
  phoneNo: "",
  email: "",
};

// ── NEW: localStorage helpers ─────────────────────────────────────────
const STORAGE_KEY = "nl_user_registered";

const getSavedUser = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const saveUser = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};
// ──────────────────────────────────────────────────────────────────────

const COUNTRY_CODES = [
  { code: "+1",   label: "+1 (USA)" },
  { code: "+44",  label: "+44 (UK)" },
  { code: "+91",  label: "+91 (India)" },
  { code: "+61",  label: "+61 (Australia)" },
  { code: "+86",  label: "+86 (China)" },
  { code: "+81",  label: "+81 (Japan)" },
  { code: "+49",  label: "+49 (Germany)" },
  { code: "+33",  label: "+33 (France)" },
  { code: "+7",   label: "+7 (Russia)" },
  { code: "+55",  label: "+55 (Brazil)" },
  { code: "+971", label: "+971 (UAE)" },
  { code: "+966", label: "+966 (Saudi Arabia)" },
  { code: "+65",  label: "+65 (Singapore)" },
  { code: "+60",  label: "+60 (Malaysia)" },
  { code: "+64",  label: "+64 (New Zealand)" },
];

const baseInput =
  "w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-60";
const inputCls = (hasError) =>
  `${baseInput} ${
    hasError
      ? "border-red-400 focus:ring-red-400"
      : "border-gray-300 focus:ring-[#335235]"
  }`;

const Broucher_Second = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBrochure, setSelectedBrochure] = useState(null);
  const [formData, setFormData] = useState(INITIAL);
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | sending | success | error
  const successTimerRef = useRef(null);

  useEffect(() => {
    emailjs.init({ publicKey: PUBLIC_KEY });
  }, []);

  useEffect(() => {
    if (status.state === "success") {
      successTimerRef.current = setTimeout(
        () => setStatus({ state: "idle", msg: "" }),
        5000
      );
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [status.state]);

  const brochures = [
    {
      title: "COSMETIC BROCHURE",
      image: cosmeticBrochureImg,
      pdf: brochurePdf,
      description:
        "Explore our premium cosmetic product range and manufacturing capabilities",
      fileSize: "2.5 MB",
    },
    {
      title: "NFPL PRESENTATION 2025",
      image: nfplPresentationImg,
      pdf: presentation,
      description: "Company overview, facilities, and achievements for 2025",
      fileSize: "3.8 MB",
    },
    {
      title: "PRODUCT LIST",
      image: nutralikeImg,
      pdf: productlist,
      description: "Complete catalog of our nutraceutical and wellness products",
      fileSize: "1.9 MB",
    },
  ];

  // ── UPDATED: skip modal if user already registered ────────────────
  const handleDownloadClick = (brochure) => {
    const savedUser = getSavedUser();

    if (savedUser) {
      // Already filled form before — download directly, no popup
      const fileName = `${slugifyFileName(brochure.title)}.pdf`;
      triggerDownload(brochure.pdf, fileName);
      return;
    }

    // First time — show form as usual
    setSelectedBrochure(brochure);
    setFieldErrors({});
    setStatus({ state: "idle", msg: "" });
    setIsModalOpen(true);
  };
  // ──────────────────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((errs) => {
        const next = { ...errs };
        delete next[name];
        return next;
      });
    }
    if (name === "phoneNo" && fieldErrors.phoneNo) {
      setFieldErrors((errs) => {
        const next = { ...errs };
        delete next.phoneNo;
        return next;
      });
    }
    if (status.state === "error") setStatus({ state: "idle", msg: "" });
  };

  const validate = () => {
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = "Please enter your first name.";
    else if (formData.firstName.trim().length < 2)
      errors.firstName = "First name must be at least 2 characters.";

    if (!formData.email.trim()) errors.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))
      errors.email = "Please enter a valid email address.";

    if (!formData.phoneNo.trim()) errors.phoneNo = "Please enter your phone number.";
    else if (!/^[\d\s()-]{6,}$/.test(formData.phoneNo.trim()))
      errors.phoneNo = "Please enter a valid phone number.";

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setStatus({
        state: "error",
        msg: "Please fix the highlighted fields and try again.",
      });
      return;
    }

    setFieldErrors({});
    setStatus({ state: "sending", msg: "" });

    const combinedPhone = `${formData.countryCode} ${formData.phoneNo.trim()}`;

    const templateParams = {
      to_email: "marketing2@nutralike.com,marketing2@nutralike.com,info@nutralike.com",
      name: formData.firstName.trim(),
      email: formData.email.trim(),
      number: combinedPhone,
      phone: combinedPhone,
      subject: `Brochure Download Request: ${selectedBrochure?.title || ""}`,
      message:
        `New brochure download request.\n\n` +
        `Brochure: ${selectedBrochure?.title || "-"}\n` +
        `Name: ${formData.firstName.trim()}\n` +
        `Company: ${formData.company.trim() || "-"}\n` +
        `Phone: ${combinedPhone}\n` +
        `Email: ${formData.email.trim()}`,
      company: formData.company.trim(),
      brochure: selectedBrochure?.title || "",
      time: new Date().toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    };

    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, {
        publicKey: PUBLIC_KEY,
      });
      setStatus({
        state: "success",
        msg: `Thank you! Your request has been received. Your ${selectedBrochure?.title || "brochure"} download will begin shortly.`,
      });

      // ── NEW: save user to localStorage so next download skips the form ──
      saveUser({
        firstName: formData.firstName.trim(),
        email: formData.email.trim(),
        phoneNo: formData.phoneNo.trim(),
        countryCode: formData.countryCode,
        company: formData.company.trim(),
      });
      // ────────────────────────────────────────────────────────────────────

      const fileName = `${slugifyFileName(selectedBrochure?.title)}.pdf`;
      triggerDownload(selectedBrochure?.pdf, fileName);

      setFormData(INITIAL);
      setTimeout(() => {
        setIsModalOpen(false);
        setSelectedBrochure(null);
      }, 1800);
    } catch (err) {
      const apiMsg =
        typeof err?.text === "string"
          ? err.text
          : typeof err?.message === "string"
          ? err.message
          : "";
      setStatus({
        state: "error",
        msg:
          apiMsg ||
          "Sorry, we couldn't submit your request right now. Please try again in a moment.",
      });
    }
  };

  const closeModal = () => {
    if (status.state === "sending") return;
    setIsModalOpen(false);
    setSelectedBrochure(null);
    setFormData(INITIAL);
    setFieldErrors({});
    setStatus({ state: "idle", msg: "" });
  };

  const isSending = status.state === "sending";

  return (
    <div className="bg-[#eef1ec] min-h-screen py-8 md:py-12 lg:py-16">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-7 lg:gap-8">
          {brochures.map((item, index) => (
            <div key={index} className="group flex flex-col h-full">

              <div className="relative bg-white overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="relative w-full overflow-hidden" style={{ aspectRatio: "3 / 4" }}>
                  <div className="shine absolute inset-0 w-full h-full">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-100 scale-90">
                      <Download
                        onClick={() => handleDownloadClick(item)}
                        className="w-12 h-12 text-white bg-[#335235] p-3 rounded-full cursor-pointer hover:bg-[#2a452c] transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 sm:mt-5 px-1 flex flex-col flex-grow">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="text-[#2d3b2f] font-bold text-sm sm:text-base md:text-lg tracking-wide leading-tight mb-2">
                      {item.title}
                    </h3>
                  </div>

                  <button
                    onClick={() => handleDownloadClick(item)}
                    className="shrink-0 p-2 rounded-full bg-white hover:bg-[#335235] transition-all duration-300 shadow-sm hover:shadow-md group/btn"
                  >
                    <Download size={18} className="text-[#335235] group-hover/btn:text-white transition-colors duration-300" />
                  </button>
                </div>

                <button
                  onClick={() => handleDownloadClick(item)}
                  className="inline-flex items-center gap-2 text-[#335235] text-xs sm:text-sm font-semibold mt-3 group-hover:gap-3 transition-all duration-300"
                >
                  Download Brochure
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* Modal Popup */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 sm:mx-0 animate-fadeIn">

            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#2d3b2f]">
                  Download Brochure
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {selectedBrochure?.title}
                </p>
              </div>
              <button
                onClick={closeModal}
                disabled={isSending}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 disabled:opacity-50"
                aria-label="Close"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Left Side */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={isSending}
                      aria-invalid={!!fieldErrors.firstName}
                      className={inputCls(!!fieldErrors.firstName)}
                      placeholder="Enter your first name"
                    />
                    {fieldErrors.firstName && (
                      <p className="text-xs text-red-600">{fieldErrors.firstName}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      disabled={isSending}
                      className={inputCls(false)}
                      placeholder="Enter your company name"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        disabled={isSending}
                        className="w-28 px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#335235] focus:border-transparent transition-all duration-200 bg-white disabled:opacity-60"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>

                      <input
                        type="tel"
                        name="phoneNo"
                        value={formData.phoneNo}
                        onChange={handleInputChange}
                        disabled={isSending}
                        aria-invalid={!!fieldErrors.phoneNo}
                        className={`flex-1 ${inputCls(!!fieldErrors.phoneNo)}`}
                        placeholder="000 0000000"
                      />
                    </div>
                    {fieldErrors.phoneNo ? (
                      <p className="text-xs text-red-600">{fieldErrors.phoneNo}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Select country code and enter your phone number
                      </p>
                    )}
                  </div>
                </div>

                {/* Right Side */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={isSending}
                      aria-invalid={!!fieldErrors.email}
                      className={inputCls(!!fieldErrors.email)}
                      placeholder="Enter your email address"
                    />
                    {fieldErrors.email && (
                      <p className="text-xs text-red-600">{fieldErrors.email}</p>
                    )}
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-800">
                      <span className="font-semibold">Note:</span> Your information will be used to send you the brochure and updates about our products.
                    </p>
                  </div>
                </div>
              </div>

              {/* Status banners */}
              {status.state === "success" && (
                <div
                  role="status"
                  aria-live="polite"
                  className="mt-5 flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-md"
                >
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}

              {status.state === "error" && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="mt-5 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-6 mt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={isSending}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSending}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-[#335235] text-white font-semibold rounded-lg hover:bg-[#2a452c] transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSending ? "SENDING..." : "Download Now"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Broucher_Second;